import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Users,
  TrendingUp,
  TrendingDown,
  Euro,
  AlertTriangle,
  UserMinus,
  ShoppingCart,
  BarChart3,
  RefreshCw,
  Calendar,
  Target,
  Star,
  UserCircle,
  Award,
  Sparkles,
  Mail,
  Search,
  ArrowUpCircle,
} from 'lucide-react'
import { customers } from '@/lib/api'
import CustomerAlertsTab from '@/components/customers/CustomerAlertsTab'
import GroupPromotionsTab from '@/components/customers/GroupPromotionsTab'
import AdvancedSearchPanel from '@/components/customers/AdvancedSearchPanel'
import { cn } from '@/lib/utils'
import { useToast } from '@/components/ui/Toast'

// ============================================================================
// COMPONENTES
// ============================================================================

function StatCard({
  title,
  value,
  change,
  changeType = 'neutral',
  icon: Icon,
  iconColor,
  iconBg,
  isLoading = false,
}: {
  title: string
  value: string | number
  change?: string
  changeType?: 'up' | 'down' | 'neutral'
  icon: any
  iconColor: string
  iconBg: string
  isLoading?: boolean
}) {
  if (isLoading) {
    return (
      <div className="stat-card">
        <div className="flex items-start justify-between">
          <div className="space-y-3 flex-1">
            <div className="skeleton h-4 w-24" />
            <div className="skeleton h-8 w-20" />
            <div className="skeleton h-4 w-32" />
          </div>
          <div className={cn('flex items-center justify-center w-12 h-12 rounded-2xl', iconBg)}>
            <Icon className={cn('w-6 h-6', iconColor)} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="stat-card group hover:shadow-soft-lg transition-all duration-300">
      <div className="flex items-start justify-between">
        <div className="space-y-3">
          <p className="stat-label">{title}</p>
          <p className="stat-value">{value}</p>
          {change && (
            <div
              className={cn(
                'stat-change',
                changeType === 'up' && 'stat-change-up',
                changeType === 'down' && 'stat-change-down',
                changeType === 'neutral' && 'text-slate-500'
              )}
            >
              {changeType === 'up' && <TrendingUp className="w-3.5 h-3.5" />}
              {changeType === 'down' && <TrendingDown className="w-3.5 h-3.5" />}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-2xl transition-transform group-hover:scale-110',
            iconBg
          )}
        >
          <Icon className={cn('w-6 h-6', iconColor)} />
        </div>
      </div>
    </div>
  )
}

function RevenueBySegmentChart({ data, isLoading }: { data: Record<string, number>; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="skeleton h-6 w-48 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="space-y-2">
              <div className="skeleton h-4 w-32" />
              <div className="skeleton h-3 w-full" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const total = Object.values(data).reduce((sum, val) => sum + val, 0)
  const sortedSegments = Object.entries(data).sort((a, b) => b[1] - a[1])

  const segmentColors: Record<string, string> = {
    'Champions (VIP)': 'bg-success-500',
    'Clientes Leales': 'bg-primary-500',
    'Prometedores': 'bg-accent-500',
    'Clientes Nuevos': 'bg-cyan-500',
    'Hibernando': 'bg-slate-400',
    'En Riesgo': 'bg-warning-500',
    'Bajando': 'bg-orange-500',
    'Perdidos': 'bg-danger-500',
    'One-Timers': 'bg-slate-500',
    'Sin Compras': 'bg-slate-300',
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Revenue por Segmento RFM
        </h3>
        <span className="text-sm text-slate-500">
          Total: {total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
        </span>
      </div>
      <div className="space-y-4">
        {sortedSegments.map(([segment, value]) => {
          const percentage = total > 0 ? (value / total) * 100 : 0
          const color = segmentColors[segment] || 'bg-slate-400'

          return (
            <div key={segment} className="group">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {segment}
                </span>
                <span className="text-sm font-semibold text-slate-900 dark:text-white">
                  {value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </span>
              </div>
              <div className="h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={cn('h-full rounded-full transition-all duration-500 ease-out', color)}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="text-xs text-slate-500 mt-1">{percentage.toFixed(1)}%</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TrendsChart({
  data,
  isLoading,
}: {
  data: Array<{ mes: string; clientes_nuevos: number; revenue: number; pedidos: number }>
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="skeleton h-6 w-48 mb-6" />
        <div className="skeleton h-48 w-full" />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Tendencia Mensual
        </h3>
        <p className="text-slate-500">No hay datos disponibles</p>
      </div>
    )
  }

  const reversed = [...data].reverse().slice(-6) // Últimos 6 meses
  const maxRevenue = Math.max(...reversed.map((d) => d.revenue))

  return (
    <div className="card p-6">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">
        Tendencia Mensual (últimos 6 meses)
      </h3>
      <div className="space-y-4">
        {reversed.map((item) => {
          const percentage = maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0

          return (
            <div key={item.mes} className="group">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {item.mes}
                </span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-slate-500">
                    <Users className="w-3 h-3 inline mr-1" />
                    {item.clientes_nuevos} nuevos
                  </span>
                  <span className="text-slate-500">
                    <ShoppingCart className="w-3 h-3 inline mr-1" />
                    {item.pedidos} pedidos
                  </span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {item.revenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
              </div>
              <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-500 ease-out"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// CONFIGURACIÓN DE GRUPOS
// ============================================================================

const CUSTOMER_GROUPS = [
  {
    id: 3,
    name: 'ClientePVP',
    label: 'Clientes PVP',
    description: 'Clientes recién registrados',
    icon: UserCircle,
    color: 'blue',
  },
  {
    id: 4,
    name: 'Cliente',
    label: 'Distribuidores',
    description: 'Distribuidores con tarifa especial',
    icon: Award,
    color: 'purple',
  },
  {
    id: 6,
    name: 'Cliente-VIP-Español',
    label: 'Clientes VIP',
    description: 'Alto volumen de compras',
    icon: Sparkles,
    color: 'amber',
  },
]

const segmentStyles: Record<
  string,
  {
    gradient: string
    icon: any
    iconColor: string
    textColor: string
    bgLight: string
    bgDark: string
  }
> = {
  'Champions (VIP)': {
    gradient: 'from-success-500 to-success-600',
    icon: Star,
    iconColor: 'text-success-600 dark:text-success-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-success-50',
    bgDark: 'bg-success-900/20',
  },
  'Clientes Leales': {
    gradient: 'from-primary-500 to-primary-700',
    icon: TrendingUp,
    iconColor: 'text-primary-600 dark:text-primary-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-primary-50',
    bgDark: 'bg-primary-900/20',
  },
  'En Riesgo': {
    gradient: 'from-warning-500 to-danger-600',
    icon: AlertTriangle,
    iconColor: 'text-warning-600 dark:text-warning-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-warning-50',
    bgDark: 'bg-warning-900/20',
  },
  Perdidos: {
    gradient: 'from-danger-600 to-danger-800',
    icon: TrendingDown,
    iconColor: 'text-danger-600 dark:text-danger-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-danger-50',
    bgDark: 'bg-danger-900/20',
  },
  'One-Timers': {
    gradient: 'from-slate-500 to-slate-600',
    icon: Users,
    iconColor: 'text-slate-600 dark:text-slate-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-slate-50',
    bgDark: 'bg-slate-900/20',
  },
  Bajando: {
    gradient: 'from-danger-500 to-danger-700',
    icon: TrendingDown,
    iconColor: 'text-danger-500 dark:text-danger-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-danger-50',
    bgDark: 'bg-danger-900/20',
  },
  Prometedores: {
    gradient: 'from-accent-500 to-accent-700',
    icon: TrendingUp,
    iconColor: 'text-accent-600 dark:text-accent-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-accent-50',
    bgDark: 'bg-accent-900/20',
  },
  Hibernando: {
    gradient: 'from-slate-400 to-slate-500',
    icon: Users,
    iconColor: 'text-slate-500 dark:text-slate-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-slate-50',
    bgDark: 'bg-slate-800/20',
  },
  'Clientes Nuevos': {
    gradient: 'from-cyan-500 to-cyan-600',
    icon: Users,
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    textColor: 'text-slate-900 dark:text-white',
    bgLight: 'bg-cyan-50',
    bgDark: 'bg-cyan-900/20',
  },
}

// ============================================================================
// TABS
// ============================================================================

type TabId = 'dashboard' | 'segments' | 'alerts' | 'promotions' | 'search' | 'campaigns' | 'carts'

const TABS: Array<{ id: TabId; label: string; icon: any; description: string }> = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3, description: 'KPIs y métricas' },
  { id: 'segments', label: 'Segmentos RFM', icon: Target, description: 'Análisis RFM' },
  { id: 'alerts', label: 'Alertas', icon: AlertTriangle, description: 'Clientes en riesgo' },
  { id: 'promotions', label: 'Promociones', icon: ArrowUpCircle, description: 'Promoción de grupos' },
  { id: 'search', label: 'Búsqueda', icon: Search, description: 'Búsqueda avanzada' },
  { id: 'campaigns', label: 'Campañas', icon: Mail, description: 'Email marketing' },
  { id: 'carts', label: 'Carritos', icon: ShoppingCart, description: 'Abandonados' },
]

// ============================================================================
// PÁGINA PRINCIPAL
// ============================================================================

export default function CustomersDashboardPage() {
  const toast = useToast()
  const [activeTab, setActiveTab] = useState<TabId>('dashboard')
  const [activeGroup, setActiveGroup] = useState(3) // Por defecto ClientePVP
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null)
  const [periodDays, setPeriodDays] = useState(30)

  // Queries
  const dashboardQuery = useQuery({
    queryKey: ['customers-dashboard', periodDays],
    queryFn: () => customers.getDashboardStats(periodDays),
  })

  const trendsQuery = useQuery({
    queryKey: ['customers-trends'],
    queryFn: () => customers.getTrends(12),
  })

  const segmentsQuery = useQuery({
    queryKey: ['rfm-segments', activeGroup],
    queryFn: () => customers.getRFMSegments(activeGroup),
    enabled: activeTab === 'segments',
  })

  const analyzeRFMMutation = useMutation({
    mutationFn: () => customers.analyzeRFM(false, activeGroup),
    onSuccess: () => {
      segmentsQuery.refetch()
      dashboardQuery.refetch()
      toast.success('Análisis RFM completado exitosamente')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const getSegmentStyle = (segmentName: string) => {
    return segmentStyles[segmentName] || segmentStyles['One-Timers']
  }

  const currentGroup = CUSTOMER_GROUPS.find((g) => g.id === activeGroup)!

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              Centro de Clientes
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Análisis, segmentación RFM y gestión de clientes
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
              className="input py-2 text-sm"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={365}>Último año</option>
            </select>
            <button
              onClick={() => analyzeRFMMutation.mutate()}
              disabled={analyzeRFMMutation.isPending}
              className="btn btn-primary"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${analyzeRFMMutation.isPending ? 'animate-spin' : ''}`}
              />
              {analyzeRFMMutation.isPending ? 'Analizando...' : 'Analizar RFM'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 dark:border-slate-700">
          <nav className="-mb-px flex space-x-8">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                    isActive
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
                  )}
                >
                  <Icon
                    className={cn(
                      '-ml-0.5 mr-2 h-5 w-5',
                      isActive ? 'text-primary-500' : 'text-slate-400 group-hover:text-slate-500'
                    )}
                  />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            <StatCard
              title="Clientes Totales"
              value={dashboardQuery.data?.clientes_total?.toLocaleString() || '...'}
              change={`+${dashboardQuery.data?.clientes_nuevos_mes || 0} este mes`}
              changeType="up"
              icon={Users}
              iconColor="text-primary-600 dark:text-primary-400"
              iconBg="bg-primary-100 dark:bg-primary-900/30"
              isLoading={dashboardQuery.isLoading}
            />
            <StatCard
              title="CLV Medio"
              value={
                dashboardQuery.data?.clv_medio
                  ? `${dashboardQuery.data.clv_medio.toLocaleString('es-ES')}€`
                  : '...'
              }
              change={`Ticket: ${dashboardQuery.data?.ticket_medio?.toLocaleString('es-ES') || 0}€`}
              changeType="neutral"
              icon={Euro}
              iconColor="text-success-600 dark:text-success-400"
              iconBg="bg-success-100 dark:bg-success-900/30"
              isLoading={dashboardQuery.isLoading}
            />
            <StatCard
              title="Tasa Retención"
              value={dashboardQuery.data?.tasa_retencion ? `${dashboardQuery.data.tasa_retencion}%` : '...'}
              change={`${dashboardQuery.data?.pedidos_mes || 0} pedidos/mes`}
              changeType={(dashboardQuery.data?.tasa_retencion ?? 0) > 20 ? 'up' : 'down'}
              icon={TrendingUp}
              iconColor="text-accent-600 dark:text-accent-400"
              iconBg="bg-accent-100 dark:bg-accent-900/30"
              isLoading={dashboardQuery.isLoading}
            />
            <StatCard
              title="Clientes en Riesgo"
              value={dashboardQuery.data?.clientes_en_riesgo?.toLocaleString() || '0'}
              change={`${dashboardQuery.data?.clientes_perdidos || 0} perdidos`}
              changeType="down"
              icon={AlertTriangle}
              iconColor="text-warning-600 dark:text-warning-400"
              iconBg="bg-warning-100 dark:bg-warning-900/30"
              isLoading={dashboardQuery.isLoading}
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RevenueBySegmentChart
              data={dashboardQuery.data?.revenue_por_segmento || {}}
              isLoading={dashboardQuery.isLoading}
            />
            <TrendsChart data={trendsQuery.data || []} isLoading={trendsQuery.isLoading} />
          </div>
        </div>
      )}

      {activeTab === 'segments' && (
        <div className="space-y-6">
          {/* Grupos de clientes */}
          <div className="flex gap-4 mb-6">
            {CUSTOMER_GROUPS.map((group) => {
              const Icon = group.icon
              const isActive = activeGroup === group.id

              return (
                <button
                  key={group.id}
                  onClick={() => {
                    setActiveGroup(group.id)
                    setSelectedSegment(null)
                  }}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
                    isActive
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{group.label}</span>
                </button>
              )
            })}
          </div>

          {/* Segments Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {segmentsQuery.isLoading ? (
              <div className="col-span-full card text-center py-8 text-slate-900 dark:text-white">
                Cargando segmentos...
              </div>
            ) : segmentsQuery.isError ? (
              <div className="col-span-full card text-center py-8 text-danger-600 dark:text-danger-400">
                Error cargando segmentos
              </div>
            ) : (
              segmentsQuery.data?.map((segment: any) => {
                const style = getSegmentStyle(segment.segment_name)
                const Icon = style.icon

                return (
                  <div
                    key={segment.segment_name}
                    className={cn(
                      'relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group',
                      style.bgLight,
                      `dark:${style.bgDark}`
                    )}
                    onClick={() => setSelectedSegment(segment.segment_name)}
                  >
                    <div className={cn('h-2 bg-gradient-to-r', style.gradient)} />
                    <div className="p-6 bg-white dark:bg-slate-800">
                      <div className="flex items-center justify-between mb-4">
                        <div className={cn('p-3 rounded-lg', style.bgLight, `dark:${style.bgDark}`)}>
                          <Icon className={cn('w-6 h-6', style.iconColor)} />
                        </div>
                        <div className="text-right">
                          <div className={cn('text-3xl font-bold', style.textColor)}>
                            {segment.customer_count}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">clientes</div>
                        </div>
                      </div>
                      <h3 className={cn('text-lg font-bold mb-2', style.textColor)}>
                        {segment.segment_name}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 min-h-[40px]">
                        {segment.description}
                      </p>
                      <button
                        className={cn(
                          'w-full py-2 px-4 rounded-lg font-medium transition-colors bg-gradient-to-r text-white hover:opacity-90',
                          style.gradient
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          // TODO: Implementar envío de campaña
                        }}
                      >
                        <Mail className="w-4 h-4 inline mr-2" />
                        Enviar Campaña
                      </button>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Selected Segment Details */}
          {selectedSegment && (
            <div className="mt-8 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div
                className={cn(
                  'px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r',
                  getSegmentStyle(selectedSegment).gradient
                )}
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">
                    Clientes en segmento: {selectedSegment} ({currentGroup.label})
                  </h2>
                  <button
                    onClick={() => setSelectedSegment(null)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Pedidos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Gastado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Score RFM
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {segmentsQuery.data
                      ?.find((s: any) => s.segment_name === selectedSegment)
                      ?.customers.slice(0, 10)
                      .map((customer: any, index: number) => (
                        <tr
                          key={customer.id || index}
                          className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                            {customer.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                            {customer.firstname} {customer.lastname}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                            <span
                              className={cn(
                                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                                customer.total_orders > 10
                                  ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                                  : customer.total_orders > 5
                                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                                  : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                              )}
                            >
                              {customer.total_orders}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                            {customer.total_spent.toLocaleString('es-ES')}€
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-600 dark:text-slate-400">
                            {customer.rfm_score || 'N/A'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Mostrando primeros 10 clientes del segmento
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'alerts' && <CustomerAlertsTab />}

      {activeTab === 'promotions' && <GroupPromotionsTab />}

      {activeTab === 'search' && <AdvancedSearchPanel />}

      {activeTab === 'campaigns' && (
        <div className="card p-8 text-center">
          <Mail className="w-12 h-12 mx-auto text-primary-500 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            Campañas de Email Marketing
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            Esta funcionalidad se implementará en la Fase 5
          </p>
        </div>
      )}

      {activeTab === 'carts' && (
        <div className="card p-8 text-center">
          <ShoppingCart className="w-12 h-12 mx-auto text-accent-500 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            Carritos Abandonados
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            Esta funcionalidad se implementará en la Fase 6
          </p>
        </div>
      )}
    </div>
  )
}
